/**
 * Historie scan jobů — přehled posledních 50 (manuální i cron) s detailem
 * toho, co se zpracovalo a jaké chyby případně padly. Otevírá se z hlavičky
 * tlačítkem "Historie".
 */
import { useState } from "react";
import { useScanJobsHistory } from "../hooks/useScanJob.js";
import type { ScanJobListItem } from "../api/rest.js";

interface Props {
  open: boolean;
  onClose: () => void;
}

const STATUS_LABELS: Record<string, { label: string; cls: string }> = {
  queued: { label: "Ve frontě", cls: "bg-slate-100 text-slate-700" },
  running: { label: "Běží", cls: "bg-blue-100 text-blue-800" },
  completed: { label: "Hotovo", cls: "bg-emerald-100 text-emerald-800" },
  completed_with_errors: {
    label: "Hotovo s chybami",
    cls: "bg-amber-100 text-amber-800",
  },
  failed: { label: "Selhalo", cls: "bg-red-100 text-red-800" },
  canceled: { label: "Zrušeno", cls: "bg-slate-100 text-slate-500" },
};

export function ScanHistoryDialog({ open, onClose }: Props) {
  const [selected, setSelected] = useState<ScanJobListItem | null>(null);
  const query = useScanJobsHistory(open);

  if (!open) return null;

  const jobs = query.data?.jobs ?? [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <h2 className="text-lg font-semibold">Historie scanů</h2>
          <button
            onClick={onClose}
            className="text-slate-500 hover:text-slate-900"
            aria-label="Zavřít"
          >
            ✕
          </button>
        </div>

        <div className="flex flex-1 min-h-0">
          <div className="flex-1 overflow-auto border-r border-slate-200">
            {query.isLoading && (
              <div className="p-4 text-sm text-slate-500">Načítám…</div>
            )}
            {query.isError && (
              <div className="p-4 text-sm text-red-600">
                Chyba: {String(query.error)}
              </div>
            )}
            {!query.isLoading && jobs.length === 0 && (
              <div className="p-4 text-sm text-slate-400">
                Žádné scan joby ještě neproběhly.
              </div>
            )}
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-left sticky top-0">
                <tr>
                  <th className="px-3 py-2">Spuštěn</th>
                  <th className="px-3 py-2">Rozsah</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2 text-right">Faktur</th>
                  <th className="px-3 py-2 text-right">Mailů</th>
                  <th className="px-3 py-2">Iniciátor</th>
                </tr>
              </thead>
              <tbody>
                {jobs.map((j) => (
                  <tr
                    key={j.id}
                    onClick={() => setSelected(j)}
                    className={`border-t border-slate-100 cursor-pointer hover:bg-slate-50 ${
                      selected?.id === j.id ? "bg-blue-50" : ""
                    }`}
                  >
                    <td className="px-3 py-2 whitespace-nowrap">
                      {fmtDateTime(j.createdAt)}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-slate-600">
                      {fmtDate(j.fromDate)}
                      {j.fromDate !== j.toDate && ` – ${fmtDate(j.toDate)}`}
                    </td>
                    <td className="px-3 py-2">
                      <StatusBadge status={j.status} />
                    </td>
                    <td className="px-3 py-2 text-right font-medium">
                      {j.extractedInvoices}
                    </td>
                    <td className="px-3 py-2 text-right text-slate-500">
                      {j.processedEmails}/{j.totalEmails}
                    </td>
                    <td className="px-3 py-2 text-slate-500 text-xs">
                      {j.requestedBy ?? "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="w-80 overflow-auto p-4 bg-slate-50">
            {!selected && (
              <div className="text-sm text-slate-400">
                Vyber řádek vlevo pro detail.
              </div>
            )}
            {selected && <JobDetail job={selected} />}
          </div>
        </div>

        <div className="flex justify-end p-3 border-t border-slate-200">
          <button
            onClick={onClose}
            className="px-3 py-1.5 border border-slate-300 rounded"
          >
            Zavřít
          </button>
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const cfg = STATUS_LABELS[status] ?? {
    label: status,
    cls: "bg-slate-100 text-slate-600",
  };
  return (
    <span
      className={`inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium ${cfg.cls}`}
    >
      {cfg.label}
    </span>
  );
}

function JobDetail({ job }: { job: ScanJobListItem }) {
  const duration =
    job.startedAt && job.finishedAt
      ? Math.round(
          (new Date(job.finishedAt).getTime() -
            new Date(job.startedAt).getTime()) /
            1000,
        )
      : null;

  return (
    <div className="space-y-3 text-sm">
      <div>
        <StatusBadge status={job.status} />
      </div>

      <KV label="Rozsah">
        {fmtDate(job.fromDate)}
        {job.fromDate !== job.toDate && ` – ${fmtDate(job.toDate)}`}
      </KV>
      <KV label="Vytvořen">{fmtDateTime(job.createdAt)}</KV>
      {job.startedAt && <KV label="Začátek">{fmtDateTime(job.startedAt)}</KV>}
      {job.finishedAt && <KV label="Konec">{fmtDateTime(job.finishedAt)}</KV>}
      {duration !== null && <KV label="Trvání">{duration}s</KV>}
      <KV label="Iniciátor">{job.requestedBy ?? "—"}</KV>

      <div className="border-t border-slate-200 pt-3 grid grid-cols-3 gap-2 text-center">
        <Stat label="Mailů" value={job.totalEmails} />
        <Stat label="Zpracováno" value={job.processedEmails} />
        <Stat label="Faktur" value={job.extractedInvoices} highlight />
      </div>

      {job.errors.length > 0 && (
        <div className="border-t border-slate-200 pt-3">
          <div className="font-medium mb-2 text-red-700">
            Chyby ({job.errors.length})
          </div>
          <ul className="space-y-1 max-h-64 overflow-auto">
            {job.errors.map((err, i) => (
              <li
                key={i}
                className="text-xs bg-red-50 border border-red-200 rounded p-2 break-words"
              >
                {err.messageId && (
                  <div className="text-slate-500 font-mono">{err.messageId}</div>
                )}
                <div className="text-red-700">{err.error}</div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function KV({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex justify-between gap-2">
      <span className="text-slate-500">{label}</span>
      <span className="text-slate-900 text-right">{children}</span>
    </div>
  );
}

function Stat({
  label,
  value,
  highlight,
}: {
  label: string;
  value: number;
  highlight?: boolean;
}) {
  return (
    <div className="bg-white border border-slate-200 rounded p-2">
      <div
        className={`text-lg font-semibold ${
          highlight ? "text-blue-700" : "text-slate-900"
        }`}
      >
        {value}
      </div>
      <div className="text-xs text-slate-500">{label}</div>
    </div>
  );
}

function fmtDate(value: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : value;
}

function fmtDateTime(value: string): string {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return `${dd}.${mm}.${yyyy} ${hh}:${mi}`;
}
